import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Score Report|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamScoreReports,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamScoreReport';
export type SPCatalogsExamScoreReportLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ButtonDisable`
	| `${LocNameStart}ButtonEnable`
	| `${LocNameStart}ButtonAdd`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}ButtonEdit`
	| `${LocNameStart}ContentInstructions`
	| `${LocNameStart}ContentNoScoreReports`
	| `${LocNameStart}LabelEnabled`
	| `${LocNameStart}LabelLanguage`
	| `${LocNameStart}LabelPassTemplate`
	| `${LocNameStart}LabelFailTemplate`;

export const spCatalogsExamScoreReportLocs: LocDefs<SPCatalogsExamScoreReportLocNames> = {
	SPCatalogsExamScoreReportTitleTab: {
		defaultValue: 'Score Report',
		description: 'Tab title',
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'Exam.ScoreReports/Catalogs.Exams.Title.ScoreReportTabTitle',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportTitlePage: {
		defaultValue: 'Score Reports',
		description: 'Page title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Pagetitle.ScoreReports',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonDisable: {
		defaultValue: 'Disable',
		description: 'Text for the disable button',
		displayPath: `${pathStart}Disable`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Disable',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonEnable: {
		defaultValue: 'Enable',
		description: 'Text for the enable button',
		displayPath: `${pathStart}Enable`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Enable',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonAdd: {
		defaultValue: 'Add',
		description: 'Text for the add button',
		displayPath: `${pathStart}Add`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Add',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Text for the cancel button',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Cancel',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonUpdate: {
		defaultValue: 'Update',
		description: 'Text for the update button',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Update',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportButtonEdit: {
		defaultValue: 'Edit',
		description: 'Text for the edit button',
		displayPath: `${pathStart}Edit`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.EditToggle',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportContentInstructions: {
		defaultValue:
			'When an English language score report is uploaded, is it used as the default score report. When an exam is translated and there is no score report uploaded for the translated language, English is used as the default score report.',
		description: 'Text for the instructions',
		displayPath: `${pathStart}Instructions`,
		displayType: 'Content',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Content.Instructions',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportContentNoScoreReports: {
		defaultValue: 'No score reports exist for this exam',
		description: 'Text for when there are no score reports.',
		displayPath: `${pathStart}No Score Reports`,
		displayType: 'Content',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Content.NoScoreReports',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportLabelEnabled: {
		defaultValue: 'Enabled',
		description: 'Label for: Enabled.',
		displayPath: `${pathStart}Enabled`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldLabel.Enabled',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportLabelLanguage: {
		defaultValue: 'Language',
		description: 'Column heading for: Language.',
		displayPath: `${pathStart}Language`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.ColumnHeading.Language',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportLabelPassTemplate: {
		defaultValue: 'Pass Template',
		description: 'Column heading for: Pass Template.',
		displayPath: `${pathStart}Pass Template`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.ColumnHeading.PassTemplate',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportLabelFailTemplate: {
		defaultValue: 'Fail Template',
		description: 'Column heading for: Fail Template.',
		displayPath: `${pathStart}Fail Template`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.ColumnHeading.FailTemplate',
		...sharedProperties,
	},
};

export type SPCatalogsExamScoreReportLocType = LocMap<SPCatalogsExamScoreReportLocNames>;
